import { constants } from '@oresundsbron/validators';
import { pipe } from 'fp-ts/lib/function';
import { map, toUndefined } from 'fp-ts/lib/Option';
import { lookup } from 'fp-ts/lib/Record';
import { FC, PropsWithChildren, useMemo } from 'react';
import { useLinks } from '~/hooks/useLinks';
import { useLocale } from '~/stores/locale';
import * as B from 'fp-ts/boolean';
import { DefaultLink } from './DefaultLink';
import { LinkProps } from './types';

type contractTypes =
  | typeof constants.OresundGo
  | typeof constants.OresundBusiness
  | typeof constants.Storebelt
  | typeof constants.OresundPendler
  | 'online_ticket';

const storebeltLinks = {
  sv: 'https://storebaelt.dk/en/terms-and-conditions/',
  da: 'https://storebaelt.dk/vilkar-og-betingelser/',
  en: 'https://storebaelt.dk/en/terms-and-conditions/',
};

export type TermsLinkProps = Omit<LinkProps, 'href'> & {
  type: contractTypes;
  className?: string;
  isOresundPay?: boolean;
};

export const TermsLink: FC<PropsWithChildren<TermsLinkProps>> = ({
  type,
  children,
  isOresundPay,
  ...rest
}) => {
  const { findLinkByCodeRef } = useLinks();
  const { locale } = useLocale();

  const getCodeRefForType = (type: contractTypes) => {
    if (isOresundPay) {
      switch (type) {
        case constants.OresundGo || constants.OresundPendler:
          return 'terms_and_conditions_private_oresundpay';
        default:
          return 'terms_and_conditions_business_oresundpay';
      }
    } else {
      switch (type) {
        case constants.OresundGo || constants.OresundPendler:
          return 'terms_and_conditions_private';
        case constants.OresundBusiness || constants.OresundBusinessPendler:
          return 'terms_and_conditions_business';
        default:
          return 'terms_and_conditions_online_ticket';
      }
    }
  };

  const href = useMemo(
    () =>
      pipe(
        type === constants.Storebelt,
        B.match(
          () =>
            pipe(
              findLinkByCodeRef(getCodeRefForType(type)),
              map((l) => `/${l.locale}${l.path}`),
              toUndefined
            ),
          () => pipe(storebeltLinks, lookup(locale), toUndefined)
        )
      ),
    [findLinkByCodeRef, locale, type]
  );

  return (
    <>
      {href && (
        <DefaultLink {...rest} href={href} target="_blank">
          {children}
        </DefaultLink>
      )}
    </>
  );
};
